import { MessageSquare, Calendar, FileText, Bell, Clock, Shield, LogIn, ExternalLink, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export function Portal() {
  const features = [
    {
      icon: MessageSquare,
      title: 'Secure Messaging',
      description: 'Communicate directly with your healthcare team through secure messaging'
    },
    {
      icon: Calendar,
      title: 'Appointment Management',
      description: 'Schedule, view, and manage your appointments online'
    },
    {
      icon: FileText,
      title: 'Medical Records Access',
      description: 'Access your medical records, test results, and treatment plans'
    },
    {
      icon: Bell,
      title: 'Notifications',
      description: 'Receive important notifications about appointments and test results'
    },
    {
      icon: Clock,
      title: '24/7 Access',
      description: 'Access your health information any time, from any device'
    },
    {
      icon: FileText,
      title: 'Online Forms',
      description: 'Complete and submit forms online before your appointments'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Patient Portal</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Access your health information securely, communicate with your care team, 
          and manage your appointments through our patient portal.
        </p>
        <div className="mt-8">
          <a
            href="https://health.healow.com/juncosneuro"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-lg"
          >
            <LogIn className="h-6 w-6 mr-2" />
            Access Patient Portal
          </a>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <div 
              key={index}
              className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all"
            >
              <Icon className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-blue-50 rounded-lg p-8 mb-16">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">First Time Users</h2>
            <p className="text-gray-600 mb-6">
              To access the patient portal, you'll need to register for an account. 
              Please follow these steps:
            </p>
            <ol className="space-y-4 text-gray-600">
              <li className="flex items-start">
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-sm mr-3 mt-1">1</span>
                <span>Call our office at (470) 239-3602 to request portal access</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-sm mr-3 mt-1">2</span>
                <span>Provide your email address to receive registration instructions</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-sm mr-3 mt-1">3</span>
                <span>Check your email for the registration link</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-sm mr-3 mt-1">4</span>
                <span>Create your secure account</span>
              </li>
            </ol>
            <div className="flex items-center text-blue-600 mt-6">
              <Shield className="h-5 w-5 mr-2" />
              <span className="font-medium">Your data is protected – HIPAA-compliant and secure</span>
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Portal Features Guide</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <HelpCircle className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                <Link
                  to="/resources/education"
                  className="text-blue-600 hover:text-blue-700"
                >
                  View our Portal User Guide
                </Link>
              </div>
              <div className="flex items-start">
                <ExternalLink className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                <a
                  href="https://health.healow.com/juncosneuro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700"
                >
                  Access Portal Help Center
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Help?</h2>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
          If you're having trouble accessing the portal or need assistance with registration, 
          our team is here to help.
        </p>
        <Link
          to="/contact"
          className="bg-white text-blue-600 border border-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
        >
          Contact Support
        </Link>
      </div>
    </div>
  );
}